import React, { useState, useEffect, useCallback } from "react";
import {
  Paper,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  IconButton,
  Chip,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Divider,
  Stack,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  alpha,
  useTheme,
  Pagination,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import {
  Visibility,
  Refresh,
  Layers,
  Search,
  FilterList,
  Delete,
  Edit,
  ArrowUpward,
  ArrowDownward,
  ExpandMore,
  ContentCopy,
  Assignment,
  Work,
  Person,
  CheckCircle,
  Numbers,
  Category,
  Texture,
  DateRange,
  Public,
  LocationOn,
  MyLocation,
  Terrain,
  Landscape,
  Agriculture,
  Grass,
  Straighten,
  Warning,
  TrendingUp,
  Water,
  Opacity,
  NaturePeople,
  EditNote,
} from "@mui/icons-material";
import api from "../../api";
import { useNavigate } from "react-router-dom";
import DataTableFrame from "../../components/common/DataTableFrame";
import PageShell, { PanelCard } from "../../components/common/PageShell";

// --- YARDIMCI BİLEŞENLER ---

const SubSectionHeader = ({ title, icon, color }) => (
  <Stack
    direction="row"
    alignItems="center"
    spacing={1.5}
    sx={{ mb: 2, mt: 3 }}
  >
    <Box
      sx={{
        display: "flex",
        p: 0.8,
        borderRadius: "8px",
        bgcolor: alpha(color, 0.1),
        color: color,
      }}
    >
      {React.cloneElement(icon, { fontSize: "small" })}
    </Box>
    <Typography
      variant="caption"
      sx={{
        fontWeight: 800,
        color: "text.secondary",
        textTransform: "uppercase",
        letterSpacing: "0.5px",
      }}
    >
      {title}
    </Typography>
    <Divider sx={{ flexGrow: 1, borderColor: alpha(color, 0.2) }} />
  </Stack>
);

const DataItem = ({ label, value, icon, color = "primary.main" }) => {
  const renderValue = () => {
    if (value === null || value === undefined || value === "") return "-";
    if (Array.isArray(value)) return value.join(", ");
    if (typeof value === "object") {
      return value.label || value.code || JSON.stringify(value);
    }
    return String(value);
  };

  return (
    <Box sx={{ p: 1.5, borderBottom: "1px solid #f0f0f0", height: "100%" }}>
      <Typography
        variant="caption"
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.5,
          fontWeight: "bold",
          textTransform: "uppercase",
          color: "text.secondary",
          fontSize: "0.65rem",
        }}
      >
        {icon
          ? React.cloneElement(icon, { sx: { fontSize: 14, color } })
          : null}{" "}
        {label.replace(/_/g, " ")}
      </Typography>
      <Typography
        variant="body2"
        sx={{
          mt: 0.5,
          fontWeight: 600,
          color: "text.primary",
          wordBreak: "break-word",
        }}
      >
        {renderValue()}
      </Typography>
    </Box>
  );
};

const SortableHeader = ({ label, field, sortBy, sortDir, onSort }) => {
  const active = sortBy === field;
  return (
    <TableCell
      sx={{
        fontWeight: 700,
        fontFamily: "Montserrat",
        cursor: "pointer",
        userSelect: "none",
        whiteSpace: "nowrap",
        bgcolor: "#F8F9FA",
      }}
      onClick={() => onSort(field)}
    >
      <Stack direction="row" alignItems="center" spacing={0.5}>
        <span>{label}</span>
        {active ? (
          sortDir === "asc" ? (
            <ArrowUpward sx={{ fontSize: 14 }} />
          ) : (
            <ArrowDownward sx={{ fontSize: 14 }} />
          )
        ) : (
          <ArrowUpward sx={{ fontSize: 14, opacity: 0.2 }} />
        )}
      </Stack>
    </TableCell>
  );
};

const ROWS_PER_PAGE = 50;

const SORT_OPTIONS = [
  { value: "sonda_no", label: "Sonda No" },
  { value: "created_at", label: "Yüklenme Tarihi" },
  { value: "tarih", label: "Saha Tarihi" },
  { value: "agent_name", label: "Personel" },
  { value: "job_name", label: "Çalışma Adı" },
  { value: "province", label: "İl" },
];

export default function SondaListPage() {
  const user = JSON.parse(localStorage.getItem("fieldUser") || "{}");
  const isAuditor = user?.role === "auditor";

  const navigate = useNavigate();
  const theme = useTheme();

  const [sondas, setSondas] = useState([]);
  const [total, setTotal] = useState(0);
  const [pageCount, setPageCount] = useState(1);
  const [loading, setLoading] = useState(false);

  const [selectedSonda, setSelectedSonda] = useState(null);
  const [openModal, setOpenModal] = useState(false);

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [sortBy, setSortBy] = useState("sonda_no");
  const [sortDir, setSortDir] = useState("asc");
  const [page, setPage] = useState(1);

  const safeParse = (data) => {
    if (!data) return {};
    if (typeof data === "object") return data;
    try {
      return JSON.parse(data);
    } catch (e) {
      return {};
    }
  };

  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 400);
    return () => clearTimeout(t);
  }, [search]);

  const fetchSondas = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/all-sonda-data", {
        params: {
          page,
          limit: ROWS_PER_PAGE,
          search: debouncedSearch,
          sortBy,
          sortDir,
        },
      });
      if (res.data.success) {
        setSondas(res.data.data);
        setTotal(res.data.total);
        setPageCount(res.data.pageCount);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [page, debouncedSearch, sortBy, sortDir]);

  useEffect(() => {
    fetchSondas();
  }, [fetchSondas]);

  const handleSort = (field) => {
    if (sortBy === field) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortBy(field);
      setSortDir("asc");
    }
    setPage(1);
  };

  const handleOpenDetail = (row) => {
    const parsedData = safeParse(row.full_data);
    setSelectedSonda({ ...row, details: parsedData });
    setOpenModal(true);
  };

  const handleDelete = async (id, sonda_no) => {
    if (
      !window.confirm(
        `"${sonda_no}" sondasını silmek istediğinize emin misiniz?\nBu işlem geri alınamaz.`,
      )
    )
      return;
    try {
      await api.delete(`/admin/sonda/${id}`);
      fetchSondas();
    } catch (err) {
      alert(
        "Silme işlemi başarısız: " +
          (err.response?.data?.message || err.message),
      );
    }
  };

  return (
    <PageShell
      title="Sonda Veri Arşivi"
      subtitle="Tüm sondaj çalışmalarının merkezi yönetimi"
      icon={<Layers />}
      chip={
        <Chip
          label={`${total} Kayıt`}
          size="small"
          color="primary"
          sx={{ fontWeight: "bold" }}
        />
      }
      actions={
        <Button
          startIcon={<Refresh />}
          variant="contained"
          color="primary"
          onClick={fetchSondas}
        >
          Yenile
        </Button>
      }
    >

      {/* FİLTRELEME & SIRALAMA */}
      <PanelCard>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr auto auto" },
            gap: 2,
            alignItems: "center",
          }}
        >
          <TextField
            fullWidth
            size="small"
            placeholder="Sonda No, Personel, Çalışma veya İl Ara..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search color="primary" />
                </InputAdornment>
              ),
            }}
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
          />
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Sırala</InputLabel>
            <Select
              value={sortBy}
              label="Sırala"
              onChange={(e) => {
                setSortBy(e.target.value);
                setPage(1);
              }}
              sx={{ borderRadius: 2 }}
            >
              {SORT_OPTIONS.map((o) => (
                <MenuItem key={o.value} value={o.value}>
                  {o.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            variant="outlined"
            size="small"
            onClick={() => {
              setSortDir((d) => (d === "asc" ? "desc" : "asc"));
              setPage(1);
            }}
            startIcon={sortDir === "asc" ? <ArrowUpward /> : <ArrowDownward />}
            sx={{ borderRadius: 2, whiteSpace: "nowrap", height: 40 }}
          >
            {sortDir === "asc" ? "Artan" : "Azalan"}
          </Button>
        </Box>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ mt: 1, display: "flex", alignItems: "center", gap: 1 }}
        >
          <FilterList fontSize="small" />
          {loading
            ? "Yükleniyor..."
            : `Toplamda ${total} kayıt — bu sayfada ${sondas.length} gösteriliyor.`}
        </Typography>
      </PanelCard>

      {/* TABLO */}
      <DataTableFrame minWidth={1400}>
          <TableHead>
            <TableRow>
              <SortableHeader
                label="Sonda No"
                field="sonda_no"
                sortBy={sortBy}
                sortDir={sortDir}
                onSort={handleSort}
              />
              <SortableHeader
                label="Personel"
                field="agent_name"
                sortBy={sortBy}
                sortDir={sortDir}
                onSort={handleSort}
              />
              <SortableHeader
                label="Çalışma Adı"
                field="job_name"
                sortBy={sortBy}
                sortDir={sortDir}
                onSort={handleSort}
              />
              <SortableHeader
                label="Konum (İl/İlçe)"
                field="province"
                sortBy={sortBy}
                sortDir={sortDir}
                onSort={handleSort}
              />
              <SortableHeader
                label="Yüklenme Tarihi"
                field="created_at"
                sortBy={sortBy}
                sortDir={sortDir}
                onSort={handleSort}
              />
              <TableCell
                align="center"
                sx={{ fontWeight: 700, bgcolor: "#F8F9FA" }}
              >
                İşlem
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                  <CircularProgress size={32} />
                </TableCell>
              </TableRow>
            ) : sondas.length > 0 ? (
              sondas.map((row) => {
                const details = safeParse(row.full_data);

                return (
                  <TableRow key={row.id} hover>
                    <TableCell>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Typography fontWeight="800" color="primary">
                          {row.sonda_no}
                        </Typography>
                        <Chip
                          label={details.sonda_tipi || "Örnek"}
                          size="small"
                          color={
                            details.sonda_tipi === "Kontrol"
                              ? "secondary"
                              : details.sonda_tipi === "İstikşafi"
                                ? "info"
                                : "default"
                          }
                          variant={
                            details.sonda_tipi === "Kontrol" ||
                            details.sonda_tipi === "İstikşafi"
                              ? "filled"
                              : "outlined"
                          }
                          sx={{ fontSize: "0.65rem", height: 18 }}
                        />
                      </Stack>
                    </TableCell>
                    <TableCell>{row.agent_name}</TableCell>
                    <TableCell>{row.job_name}</TableCell>
                    <TableCell>
                      {row.province} / {details.ilce || row.district || "-"}
                    </TableCell>
                    <TableCell>
                      {new Date(row.created_at).toLocaleDateString("tr-TR")}
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="Görüntüle">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleOpenDetail(row)}
                          sx={{
                            bgcolor: alpha(theme.palette.primary.main, 0.1),
                            mr: 0.5,
                          }}
                        >
                          <Visibility fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {!isAuditor && (
                        <>
                          <Tooltip title="Düzenle">
                            <IconButton
                              size="small"
                              color="secondary"
                              onClick={() =>
                                navigate(`/admin/edit-sonda/${row.id}`)
                              }
                              sx={{
                                bgcolor: alpha(
                                  theme.palette.secondary.main,
                                  0.1,
                                ),
                                mr: 0.5,
                              }}
                            >
                              <Edit fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Sil">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDelete(row.id, row.sonda_no)}
                              sx={{
                                bgcolor: alpha(theme.palette.error.main, 0.1),
                              }}
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                  <Typography
                    color="text.secondary"
                    sx={{ fontStyle: "italic" }}
                  >
                    Kayıt bulunamadı.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
      </DataTableFrame>

      {/* SAYFALAMA */}
      {total > 0 && (
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ mt: 2, flexWrap: "wrap", gap: 1 }}
        >
          <Typography variant="caption" color="text.secondary">
            Sayfa {page} / {pageCount} — Gösterilen:{" "}
            {(page - 1) * ROWS_PER_PAGE + 1}–
            {Math.min(page * ROWS_PER_PAGE, total)} / {total}
          </Typography>
          <Pagination
            count={pageCount}
            page={page}
            onChange={(_, v) => setPage(v)}
            color="primary"
            shape="rounded"
            showFirstButton
            showLastButton
          />
        </Stack>
      )}

      {/* DETAY MODAL */}
      <Dialog
        open={openModal}
        onClose={() => setOpenModal(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { borderRadius: "20px" } }}
      >
        <DialogTitle sx={{ bgcolor: "primary.main", color: "#fff" }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="h6" fontWeight="800">
              Sonda Teknik Detayı: {selectedSonda?.sonda_no}
            </Typography>
            <IconButton
              onClick={() => setOpenModal(false)}
              sx={{ color: "#fff" }}
            >
              <ExpandMore sx={{ transform: "rotate(180deg)" }} />
            </IconButton>
          </Stack>
        </DialogTitle>

        <DialogContent sx={{ p: 4, bgcolor: "#fcfcfd" }}>
          {selectedSonda && (
            <Box>
              {/* BAĞLI KONTROL SONDALARI (Örnek veya İstikşafi ise) */}
              {(selectedSonda.details.sonda_tipi === "Örnek" ||
                selectedSonda.details.sonda_tipi === "İstikşafi") && (
                <Box sx={{ mt: 0, mb: 3 }}>
                  {(() => {
                    const linkedKontrolSondas = sondas.filter((s) => {
                      const d = safeParse(s.full_data);
                      return (
                        d.sonda_tipi === "Kontrol" &&
                        d.benzer_sonda_no === selectedSonda.sonda_no
                      );
                    });

                    if (linkedKontrolSondas.length === 0) return null;

                    return (
                      <Paper
                        sx={{
                          p: 2,
                          bgcolor: alpha(theme.palette.secondary.main, 0.05),
                          border: "1px dashed",
                          borderColor: "secondary.main",
                          borderRadius: 3,
                        }}
                      >
                        <Typography
                          variant="subtitle2"
                          color="secondary.main"
                          fontWeight="bold"
                          mb={1.5}
                          display="flex"
                          alignItems="center"
                          gap={1}
                        >
                          <ContentCopy fontSize="small" /> BU ÖRNEĞE BAĞLI
                          KONTROL SONDALARI
                        </Typography>
                        <Stack
                          direction="row"
                          spacing={1}
                          flexWrap="wrap"
                          useFlexGap
                        >
                          {linkedKontrolSondas.map((ls) => (
                            <Chip
                              key={ls.id}
                              label={ls.sonda_no}
                              color="secondary"
                              variant="outlined"
                              onClick={() => handleOpenDetail(ls)}
                              sx={{
                                fontWeight: "bold",
                                cursor: "pointer",
                                "&:hover": {
                                  bgcolor: alpha(
                                    theme.palette.secondary.main,
                                    0.1,
                                  ),
                                },
                              }}
                            />
                          ))}
                        </Stack>
                      </Paper>
                    );
                  })()}
                </Box>
              )}

              {/* 1. PROJE VE KİMLİK BİLGİLERİ */}
              <SubSectionHeader
                title="Proje & Kimlik Bilgileri"
                icon={<Assignment />}
                color={theme.palette.primary.main}
              />
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    sm: "1fr 1fr",
                    md: "repeat(5, 1fr)",
                  },
                  gap: 1,
                  mb: 3,
                }}
              >
                <DataItem
                  label="Çalışma Adı"
                  value={selectedSonda.job_name}
                  icon={<Work />}
                />
                <DataItem
                  label="Personel"
                  value={selectedSonda.agent_name}
                  icon={<Person />}
                />
                <DataItem
                  label="Sonda Tipi"
                  value={selectedSonda.details.sonda_tipi}
                  icon={<CheckCircle />}
                  color={
                    selectedSonda.details.sonda_tipi === "Kontrol"
                      ? "secondary.main"
                      : "primary.main"
                  }
                />
                <DataItem
                  label="Sonda No"
                  value={selectedSonda.sonda_no}
                  icon={<Numbers />}
                />
                <DataItem
                  label="Grup No"
                  value={selectedSonda.details.grup_no}
                  icon={<Category />}
                />
                {selectedSonda.details.sonda_tipi === "Kontrol" && (
                  <DataItem
                    label="Kopyalanan Sonda"
                    value={selectedSonda.details.benzer_sonda_no}
                    icon={<ContentCopy />}
                    color="secondary.main"
                  />
                )}
                <DataItem
                  label="Çuval No"
                  value={selectedSonda.details.cuval_no}
                  icon={<Texture />}
                />
                <DataItem
                  label="Alma Sırası"
                  value={selectedSonda.details.alma_sirasi}
                  icon={<FilterList />}
                />
                <DataItem
                  label="Benzer Profil"
                  value={selectedSonda.details.benzer_profil}
                  icon={<Assignment />}
                />
                <DataItem
                  label="Tarih"
                  value={selectedSonda.details.tarih}
                  icon={<DateRange />}
                />
              </Box>

              {/* 2. KONUM BİLGİLERİ */}
              <SubSectionHeader
                title="Konum Bilgileri"
                icon={<LocationOn />}
                color="#ef6c00"
              />
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    sm: "1fr 1fr",
                    md: "repeat(5, 1fr)", // Sütun sayısını 4'ten 5'e çıkardık ki mahalle de sığsın
                  },
                  gap: 1,
                  mb: 3,
                }}
              >
                <DataItem
                  label="Şehir"
                  value={
                    selectedSonda.details.il ||
                    selectedSonda.details.sehir ||
                    selectedSonda.province
                  }
                  icon={<Public />}
                />
                <DataItem
                  label="İlçe"
                  value={selectedSonda.details.ilce || selectedSonda.district}
                  icon={<LocationOn />}
                />
                {/* YENİ EKLENDİ */}
                <DataItem
                  label="Mahalle"
                  value={selectedSonda.details.mahalle}
                  icon={<LocationOn />}
                />
                <DataItem
                  label="Koordinat X"
                  value={selectedSonda.details.koordinat_x}
                  icon={<MyLocation />}
                />
                <DataItem
                  label="Koordinat Y"
                  value={selectedSonda.details.koordinat_y}
                  icon={<MyLocation />}
                />
              </Box>

              {/* 3. ARAZİ VE TOPRAK KARAKTERİSTİĞİ */}
              <SubSectionHeader
                title="Arazi ve Toprak Karakteristiği"
                icon={<Terrain />}
                color="#2e7d32"
              />
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    sm: "1fr 1fr",
                    md: "repeat(3, 1fr)",
                  },
                  gap: 1,
                }}
              >
                <DataItem
                  label="Arazi Kullanımı"
                  value={selectedSonda.details.arazi_kullanim_sinif}
                  icon={<Agriculture />}
                />
                <DataItem
                  label="BTG"
                  value={selectedSonda.details.btg}
                  icon={<Layers />}
                  color="success.main"
                />
                <DataItem
                  label="Ana Materyal"
                  value={selectedSonda.details.ana_materyal}
                  icon={<Terrain />}
                />
                <DataItem
                  label="Fizyografya"
                  value={selectedSonda.details.fizyografya}
                  icon={<Landscape />}
                />
              </Box>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    sm: "1fr 1fr",
                    md: "repeat(4, 1fr)",
                  },
                  gap: 1,
                  mt: 1,
                  mb: 3,
                }}
              >
                <DataItem
                  label="Eğim (%)"
                  value={selectedSonda.details.egim}
                  icon={<TrendingUp />}
                />
                <DataItem
                  label="Drenaj"
                  value={selectedSonda.details.drenaj}
                  icon={<Opacity />}
                />
                <DataItem
                  label="Su Erozyonu"
                  value={selectedSonda.details.su_erozyonu}
                  icon={<Warning />}
                />
                <DataItem
                  label="Derinlik (cm)"
                  value={selectedSonda.details.derinlik}
                  icon={<Straighten />}
                />
                <DataItem
                  label="Taşlılık"
                  value={selectedSonda.details.taslilik}
                  icon={<Terrain />}
                />
                <DataItem
                  label="Kayalılık"
                  value={selectedSonda.details.kayalilik}
                  icon={<Terrain />}
                />
                <DataItem
                  label="Çakıllılık"
                  value={selectedSonda.details.cakillilik}
                  icon={<Terrain />}
                />
                <DataItem
                  label="Ürün / Örtü"
                  value={selectedSonda.details.urun}
                  icon={<Agriculture />}
                />
              </Box>

              {/* 4. KATMAN (HORİZON) ANALİZLERİ */}
              <Typography
                variant="h6"
                fontWeight="bold"
                sx={{ mb: 2, color: "text.secondary" }}
              >
                KATMAN (HORİZON) ANALİZLERİ
              </Typography>
              <DataTableFrame
                size="small"
                minWidth={650}
                maxHeight={420}
                sx={{ borderRadius: 2, mb: 3 }}
              >
                  <TableHead sx={{ bgcolor: "#fafafa" }}>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 800 }}>Sembol</TableCell>
                      <TableCell sx={{ fontWeight: 800 }}>
                        Derinlik (cm)
                      </TableCell>
                      <TableCell sx={{ fontWeight: 800 }}>Tekstür</TableCell>
                      <TableCell sx={{ fontWeight: 800 }}>Kireç</TableCell>
                      <TableCell sx={{ fontWeight: 800 }}>Tuzluluk</TableCell>
                      <TableCell sx={{ fontWeight: 800 }}>Alkalilik</TableCell>
                      <TableCell sx={{ fontWeight: 800 }}>Bor</TableCell>
                      <TableCell sx={{ fontWeight: 800 }}>
                        Munsell (Kuru/Yaş)
                      </TableCell>
                      <TableCell sx={{ fontWeight: 800 }}>Gözlem</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {selectedSonda.details.horizons?.map((h, idx) => (
                      <TableRow key={idx} hover>
                        <TableCell>
                          <Typography variant="subtitle2" color="primary.main">
                            {h.sembol ||
                              (idx === 0 ? "Yüzey" : `${idx + 1}. Numune`)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight="700">
                            {h.derinlik_min} - {h.derinlik_max} cm{" "}
                            {h.derinlik_arti ? "+" : ""}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={h.tekstur || "-"}
                            size="small"
                            variant="outlined"
                            color="success"
                          />
                        </TableCell>
                        <TableCell>{h.kirec || "-"}</TableCell>
                        <TableCell>{h.tuzluluk || "-"}</TableCell>
                        <TableCell>{h.alkalilik || "-"}</TableCell>
                        <TableCell>{h.bor || "-"}</TableCell>
                        <TableCell>
                          <Typography
                            variant="caption"
                            sx={{ display: "block" }}
                          >
                            K: {h.renk_kod_kuru || "-"} (
                            {h.renk_sinif_kuru || "-"})
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{ display: "block" }}
                          >
                            Y: {h.renk_kod_yas || "-"} (
                            {h.renk_sinif_yas || "-"})
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ maxWidth: 200 }}>
                          <Typography
                            variant="caption"
                            sx={{ fontStyle: "italic" }}
                          >
                            {h.gozlem || "-"}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
              </DataTableFrame>

              {/* 5. GENEL NOTLAR */}
              <Paper
                sx={{
                  p: 2,
                  bgcolor: alpha(theme.palette.info.main, 0.05),
                  border: "1px dashed",
                  borderColor: "info.main",
                  borderRadius: 3,
                }}
              >
                <Typography
                  variant="subtitle2"
                  color="info.main"
                  sx={{
                    fontWeight: 700,
                    mb: 1,
                    fontFamily: "Montserrat",
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <EditNote /> GENEL SAHA NOTLARI
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ fontStyle: "italic", color: "text.primary" }}
                >
                  {selectedSonda.details.gozlemler ||
                    "Bu çalışma için eklenmiş bir not bulunmamaktadır."}
                </Typography>
              </Paper>
            </Box>
          )}
        </DialogContent>
        <DialogActions
          sx={{ p: 2, bgcolor: "#f8f9fa", borderTop: "1px solid #e0e0e0" }}
        >
          <Button
            color="inherit"
            sx={{ fontWeight: 700 }}
            onClick={() => setOpenModal(false)}
          >
            Kapat
          </Button>
        </DialogActions>
      </Dialog>
    </PageShell>
  );
}
